import type { Settings } from '@copilot/shared';
import { SettingsSchema } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { settingsApi } from '../api/admin';
import { Button, Card, ChipsInput, ErrorBanner, Field, Input, PageSkeleton, PageHeader, Select } from '../components';
import { LANGUAGE_OPTIONS } from '../lib/labels';
import { normalizeDomain } from '../lib/text';

export function SettingsPage() {
  const query = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });
  return (
    <div>
      <PageHeader title="Settings" description="Organisation-wide defaults. Changes apply to new calls." />
      {query.isLoading && <PageSkeleton />}
      {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && <SettingsForm key={JSON.stringify(query.data)} initial={query.data} />}
    </div>
  );
}

/** Number inputs are edited as strings so users can clear and retype them. */
type FormState = Omit<Settings, NumberKey> & Record<NumberKey, string>;
type NumberKey =
  | 'transcriptRetentionDays'
  | 'summaryRetentionDays'
  | 'suggestionIntervalSec'
  | 'defaultMonthlyLimitUsd'
  | 'softLimitPct'
  | 'askBufferUsd';
const NUMBER_KEYS: NumberKey[] = [
  'transcriptRetentionDays',
  'summaryRetentionDays',
  'suggestionIntervalSec',
  'defaultMonthlyLimitUsd',
  'softLimitPct',
  'askBufferUsd',
];

function toForm(s: Settings): FormState {
  const f = { ...s } as unknown as FormState;
  for (const k of NUMBER_KEYS) f[k] = String(s[k]);
  return f;
}

function fromForm(f: FormState): unknown {
  const out: Record<string, unknown> = { ...f };
  for (const k of NUMBER_KEYS) out[k] = f[k].trim() === '' ? NaN : Number(f[k]);
  return out;
}

const FIELD_HINTS: Record<string, string> = {
  transcriptRetentionDays: '1–3650 days',
  summaryRetentionDays: '1–3650 days',
  suggestionIntervalSec: '10–120 seconds',
  defaultMonthlyLimitUsd: '0–100000',
  softLimitPct: '10–100%',
  askBufferUsd: '0–1000',
};

function SettingsForm({ initial }: { initial: Settings }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toForm(initial));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: settingsApi.put,
    onSuccess: (saved) => queryClient.setQueryData(['settings'], saved ?? undefined),
  });
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(initial));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = SettingsSchema.safeParse(fromForm(form));
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        errs[key] ??= FIELD_HINTS[key] ? `Must be ${FIELD_HINTS[key]}.` : issue.message;
      }
      setErrors(errs);
      return;
    }
    setErrors({});
    save.mutate(parsed.data, {
      onSuccess: (saved) => {
        if (!saved) void queryClient.invalidateQueries({ queryKey: ['settings'] });
      },
    });
  };

  const num = (k: NumberKey, label: string, extra: { step?: string; suffix?: string; hint?: ReactNode } = {}) => (
    <Field label={label} error={errors[k]} hint={extra.hint ?? FIELD_HINTS[k]}>
      <div className="flex items-center gap-2">
        <Input type="number" step={extra.step ?? '1'} value={form[k]} onChange={(e) => set(k, e.target.value)} aria-invalid={!!errors[k]} className="w-32" />
        {extra.suffix && <span className="text-sm text-muted">{extra.suffix}</span>}
      </div>
    </Field>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <Card title="Data retention" description="Older data is deleted automatically by the server.">
        <div className="grid gap-4 sm:grid-cols-2">
          {num('transcriptRetentionDays', 'Transcript retention', { suffix: 'days' })}
          {num('summaryRetentionDays', 'Summary retention', { suffix: 'days' })}
        </div>
      </Card>

      <Card title="Live assistance">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Default language" error={errors.defaultLanguage} hint="Speech recognition language for new users.">
            <Select value={form.defaultLanguage} onChange={(e) => set('defaultLanguage', e.target.value)} className="w-56">
              {!LANGUAGE_OPTIONS.some((o) => o.value === form.defaultLanguage) && <option value={form.defaultLanguage}>{form.defaultLanguage}</option>}
              {LANGUAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({o.value})
                </option>
              ))}
            </Select>
          </Field>
          {num('suggestionIntervalSec', 'Suggestion cadence', { suffix: 'seconds', hint: 'How often tips refresh during a call (10–120 s).' })}
        </div>
      </Card>

      <Card title="Cost limits" description="Per-rep monthly AI spend. Individual limits can be set on the Users page.">
        <div className="grid gap-4 sm:grid-cols-3">
          {num('defaultMonthlyLimitUsd', 'Default monthly limit', { step: '0.01', suffix: 'USD' })}
          {num('softLimitPct', 'Soft limit warning at', { suffix: '%', hint: 'Reps are warned at this share of their limit (10–100%).' })}
          {num('askBufferUsd', 'Ask buffer', {
            step: '0.01',
            suffix: 'USD',
            hint: 'Extra spend allowed for manual "Ask" questions after the limit is reached (0–1000).',
          })}
        </div>
      </Card>

      <Card title="Domains">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Allowed signup domains" error={errors.allowedSignupDomains} hint="Leave empty to let anyone sign up. Otherwise only these email domains can.">
            <ChipsInput value={form.allowedSignupDomains} onChange={(v) => set('allowedSignupDomains', v)} normalize={normalizeDomain} placeholder="clustox.com" />
          </Field>
          <Field label="Internal domains" error={errors.internalDomains} hint="Treated as our side when matching calendar attendees to clients.">
            <ChipsInput value={form.internalDomains} onChange={(v) => set('internalDomains', v)} normalize={normalizeDomain} placeholder="clustox.com" />
          </Field>
        </div>
      </Card>

      <ErrorBanner error={save.error} />
      <div className="flex items-center justify-end gap-3">
        {save.isSuccess && !dirty && <span className="text-xs text-good">Saved.</span>}
        {dirty && (
          <Button variant="ghost" onClick={() => setForm(toForm(initial))}>
            Discard changes
          </Button>
        )}
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
          Save settings
        </Button>
      </div>
    </form>
  );
}
